import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft } from 'lucide-react';
import { Subject } from '@/types';
import { testApi, questionApi } from '@/lib/api';
import { EnhancedTestCreateForm } from '@/components/tests/EnhancedTestCreateForm';
import { useToast } from '@/hooks/use-toast';
import { getErrorInfo } from '@/lib/errorUtils';
import TestCreationProgress from '@/components/tests/TestCreationProgress';

const CreateTestPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const errorRef = useRef<HTMLDivElement>(null);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(false);
  const [createTestError, setCreateTestError] = useState('');

  useEffect(() => {
    loadSubjects();
  }, []);

  const loadSubjects = async () => {
    try {
      console.log('CreateTestPage: Loading subjects from questions...');
      const subjectNames = await questionApi.getSubjects();
      console.log('CreateTestPage: Subjects response:', subjectNames);

      let subjectsData: Subject[] = [];
      if (subjectNames && Array.isArray(subjectNames)) {
        // Convert subject names to Subject objects
        subjectsData = subjectNames.map((name: string, index: number) => ({
          id: `subject_${index}`,
          name: name,
          isActive: true
        }));
      }

      console.log('CreateTestPage: Processed subjects:', subjectsData);
      setSubjects(subjectsData);

      if (subjectsData.length === 0) {
        console.warn('CreateTestPage: No subjects found in questions');
        toast({
          title: 'Warning',
          description: 'No subjects found. Please ensure questions exist in the system.',
          variant: 'default',
        });
      }
    } catch (error) {
      console.error('CreateTestPage: Failed to load subjects:', error);
      toast({
        title: 'Error',
        description: 'Failed to load subjects. Please check if questions exist in the system.',
        variant: 'destructive',
      });
    }
  };

  const handleCreateTest = async (testData: any) => {
    try {
      setLoading(true);
      setCreateTestError('');

      let createdTest;
      if (testData.type === 'standard') {
        // Create test from standard configuration
        createdTest = await testApi.createFromConfiguration(testData.configurationId, {
          title: testData.title,
          description: testData.description
        });
      } else {
        // Create custom test with marking scheme
        const markingScheme = testData.markingScheme || {
          correctMarks: 4,
          incorrectMarks: -1,
          unansweredMarks: 0,
          negativeMarkingEnabled: true,
          maxMarks: testData.totalQuestions * 4
        };
        
        // Calculate maxMarks based on total questions and correct marks
        markingScheme.maxMarks = testData.totalQuestions * markingScheme.correctMarks;

        createdTest = await testApi.createCustom({
          title: testData.title,
          description: testData.description,
          subjectIds: testData.subjectIds,
          subjectNames: testData.subjectNames,
          topicIds: testData.topicIds,
          topicNames: testData.topicNames,
          duration: testData.duration,
          totalQuestions: testData.totalQuestions,
          difficultyDistribution: testData.difficultyDistribution,
          difficultyDistributionEnabled: testData.difficultyDistributionEnabled,
          kitNumbers: testData.kitNumbers,
          tagFilters: testData.tagFilters,
          selectedQuestionIds: testData.selectedQuestionIds,
          autoGenerateRemainingQuestions: testData.autoGenerateRemainingQuestions,
          markingScheme: markingScheme
        });
      }

      if (createdTest?.id) {
        // Navigate to question preview page
        navigate(`/tests/preview/${createdTest.id}`, {
          state: {
            testTitle: createdTest.title,
            mode: 'create'
          }
        });
      } else {
        throw new Error('Test creation failed - no test ID returned');
      }
    } catch (error: any) {
      console.error('Failed to create test:', error);

      // Debug: Log the full error structure
      console.log('Error structure:', {
        error: error,
        response: error?.response,
        responseData: error?.response?.data,
        responseStatus: error?.response?.status,
        message: error?.message
      });

      // Use the error utility to get user-friendly error message
      const errorInfo = getErrorInfo(error);
      setCreateTestError(errorInfo.message);

      // Scroll to error message after a brief delay to ensure it's rendered
      setTimeout(() => {
        if (errorRef.current) {
          errorRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
          errorRef.current.focus();
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/tests');
  };

  return (
    <div>
      {/* Progress Indicator */}
      <TestCreationProgress currentStep="create" />

      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Header */}
        <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/tests')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Tests
          </Button>
        </div>
        <h1 className="text-3xl font-bold">Create New Test</h1>
        <p className="text-muted-foreground mt-2">
          Create a new test with dynamic question selection based on difficulty distribution.
        </p>
      </div>

      {/* Error Alert */}
      {createTestError && (
        <Alert ref={errorRef} variant="destructive" className="mb-6" tabIndex={-1} role="alert">
          <AlertDescription>{createTestError}</AlertDescription>
        </Alert>
      )}

      {/* Test Creation Form */}
      <Card>
        <CardHeader>
          <CardTitle>Test Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <EnhancedTestCreateForm
            subjects={subjects}
            onSubmit={handleCreateTest}
            onCancel={handleCancel}
            loading={loading}
          />
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default CreateTestPage;
